import c from "classnames";
import dayjs from "dayjs";
import { useCallback, useState } from "react";
import { useRef } from "react";
import { useEffect } from "react";
import { useMemo } from "react";
import { Responsive } from "react-grid-layout";

import { ResourceGroupEmployeeUsageLineChart } from "@components/Charts";
import {
  ApplicationDashboardFilterForm,
  ApplicationDashboardFilterFormRef,
  ApplicationDashboardFilterFormValues,
} from "@components/Forms";
import {
  ResourceGroupAuthenticatedUsersStat,
  ResourceGroupTotalUsersStat,
  ResourceGroupUsageTimeStat,
} from "@components/Stats";
import { ResourceGroupEmployeesTable } from "@components/Tables/ResourceGroupEmployeesTable/ResourceGroupEmployeesTable";

import { Box } from "@design-system/Box/Box";
import { Card } from "@design-system/Card/Card";
import { IconButton } from "@design-system/IconButton/IconButton";
import { SlideInPanel } from "@design-system/SlideInPanel";
import { Text } from "@design-system/Text/Text";

import { useApplicationDashboardDataQuery } from "./ApplicationDashboard.g";
import classes from "./ApplicationDashboard.module.css";
import "react-grid-layout/css/styles.css";
import "react-resizable/css/styles.css";

const layout = [
  { i: "a", x: 0, y: 0, w: 4, h: 4 },
  { i: "b", x: 4, y: 0, w: 4, h: 4 },
  { i: "c", x: 8, y: 0, w: 4, h: 4 },
  { i: "e", x: 0, y: 3, w: 12, h: 12 },
  { i: "f", x: 0, y: 15, w: 12, h: 20 },
];

const layoutMd = [
  { i: "a", x: 0, y: 0, w: 12, h: 4 },
  { i: "b", x: 0, y: 4, w: 6, h: 4 },
  { i: "c", x: 6, y: 4, w: 6, h: 4 },
  { i: "e", x: 0, y: 8, w: 12, h: 12 },
  { i: "f", x: 0, y: 20, w: 12, h: 20 },
];

const layoutSm = [
  { i: "a", x: 0, y: 0, w: 12, h: 4 },
  { i: "b", x: 0, y: 4, w: 12, h: 4 },
  { i: "c", x: 0, y: 8, w: 12, h: 4 },
  { i: "e", x: 0, y: 12, w: 12, h: 12 },
  { i: "f", x: 0, y: 24, w: 12, h: 20 },
];

const layouts = {
  lg: layout,
  md: layout,
  sm: layoutMd,
  xs: layoutMd,
  xxs: layoutSm,
};

type Props = {
  applicationId: string; // This is the client application ID
  clientId: string;
};

export const ApplicationDashboard = ({ applicationId, clientId }: Props) => {
  const ref1 = useRef<HTMLDivElement>(null);
  const [width, setWidth] = useState(0);
  // eslint-disable-next-line @typescript-eslint/no-unused-vars -- enable once we have a way to edit the layout
  const [isEditingLayout, _setIsEditingLayout] = useState(false);

  const [filterValues, setFilterValues] =
    useState<ApplicationDashboardFilterFormValues>({
      date_Gte: undefined,
      date_Lte: undefined,
    });

  const filterFormRef = useRef<ApplicationDashboardFilterFormRef>(null);

  const areFiltersActive = useMemo(() => {
    return Boolean(
      filterValues.date_Gte || filterValues.date_Lte,
    );
  }, [filterValues]);

  // Date range state - default to last 30 days
  const [dateRange, setDateRange] = useState<[Date | null, Date | null]>(() => {
    const endDate = new Date();
    const startDate = dayjs().subtract(30, "days").toDate();
    return [startDate, endDate];
  });

  const [isFilterPanelOpen, setIsFilterPanelOpen] = useState(false);

  // Calculate days difference for the chart
  const daysToShow = useMemo(() => {
    if (!dateRange[0] || !dateRange[1]) return 30;
    const start = dayjs(dateRange[0]);
    const end = dayjs(dateRange[1]);
    return Math.max(1, end.diff(start, "days") + 1);
  }, [dateRange]);

  const handleFilterClick = () => {
    setIsFilterPanelOpen(true);
  };

  const handleApplyFilters = useCallback(() => {
    if (filterFormRef.current) {
      const currentFormValues = filterFormRef.current.getCurrentValues();
      setFilterValues(currentFormValues);
      setDateRange([
        currentFormValues.date_Gte ? dayjs(currentFormValues.date_Gte).toDate() : null,
        currentFormValues.date_Lte ? dayjs(currentFormValues.date_Lte).toDate() : null,
      ]);
    }
  }, []);

  const handleFilterChange = useCallback(
    (newValues: ApplicationDashboardFilterFormValues) => {
      setFilterValues(newValues);
      setIsFilterPanelOpen(false);
      setDateRange([
        newValues.date_Gte ? dayjs(newValues.date_Gte).toDate() : null,
        newValues.date_Lte ? dayjs(newValues.date_Lte).toDate() : null,
      ]);
    },
    [],
  );

  const handleCloseFilterPanel = useCallback(() => {
    setIsFilterPanelOpen(false);
  }, []);

  const handleFiltersReset = useCallback(() => {
    const endDate = new Date();
    const startDate = dayjs().subtract(30, "days").toDate();

    setFilterValues({
      date_Gte: undefined,
      date_Lte: undefined,
    });
    setDateRange([startDate, endDate]);
  }, []);

  // Fetch the client application to get the resource group ID
  const { data, isLoading } = useApplicationDashboardDataQuery({
    clientId,
    clientApplicationId: applicationId,
  });

  // Extract resource group ID from the fetched client application
  const resourceGroupId = useMemo(() => {
    return data?.clientApplication?.resource?.resourceGroup?.id;
  }, [data]);

  // Extract stats from the fetched client application
  const stats = useMemo(() => {
    return data?.clientApplication?.resourceGroupApplications?.stats;
  }, [data]);

  useEffect(() => {
    const handleResize = () => {
      if (ref1.current) {
        setWidth(ref1.current.offsetWidth);
      }
    };

    // Only set up observer if we have the resource group
    if (!resourceGroupId) {
      return () => {}; // Return empty cleanup function
    }

    // Set initial width immediately
    handleResize();

    const observer = new ResizeObserver(handleResize);
    if (ref1.current) {
      observer.observe(ref1.current);
    }

    return () => observer.disconnect();
  }, [resourceGroupId]); // Re-run when resource group is available

  // Don't render until we have the resource group ID
  if (isLoading || !resourceGroupId) {
    return null;
  }

  return (
    <div className={classes.container} ref={ref1}>
      <Responsive
        layouts={layouts}
        className={classes.responsive}
        rowHeight={12}
        width={width}
        cols={{ lg: 12, md: 12, sm: 12, xs: 12, xxs: 12 }}
        isResizable={isEditingLayout}
        isDraggable={isEditingLayout}
        autoSize
        containerPadding={[0, 0]}
      >
        <div key="a" className={classes.card}>
          <div className={c({ [classes.editing]: isEditingLayout })}>
            <ResourceGroupUsageTimeStat
              lastSessionTime={stats?.lastSessionTime}
            />
          </div>
        </div>
        <div key="b" className={classes.card}>
          <div className={c({ [classes.editing]: isEditingLayout })}>
            <ResourceGroupTotalUsersStat totalUsers={stats?.totalEmployees} />
          </div>
        </div>
        <div key="c" className={classes.card}>
          <div className={c({ [classes.editing]: isEditingLayout })}>
            <ResourceGroupAuthenticatedUsersStat
              totalEmployeesAuthenticated={stats?.totalEmployeesAuthenticated}
            />
          </div>
        </div>
        <div key="e" className={classes.card}>
          <div className={c({ [classes.editing]: isEditingLayout })}>
            <Card>
              <Card.Header>
                <Card.Header.Title>
                  Application usage by users
                </Card.Header.Title>
              </Card.Header>
              <Card.Body grow>
                <div className={classes.chart}>
                  <ResourceGroupEmployeeUsageLineChart
                    clientApplicationId={applicationId}
                    daysToShow={daysToShow}
                    startDate={dateRange[0] || undefined}
                    endDate={dateRange[1] || undefined}
                  />
                </div>
              </Card.Body>
            </Card>
          </div>
        </div>
        <div key="f" className={classes.card}>
          <div className={c({ [classes.editing]: isEditingLayout })}>
            <Card grow stretch>
              <Card.Header>
                <Card.Header.Title>Users for selected range</Card.Header.Title>
                <Card.Header.Actions>
                  <Box direction="row" gap={8} align="center">
                    {areFiltersActive && (
                      <Text size={12} color={"textSecondary"}>
                        Filters are applied
                      </Text>
                    )}
                    <IconButton
                      size="sm"
                      icon="faFilter"
                      onClick={handleFilterClick}
                      outline={"sm"}
                      showBubble={areFiltersActive}
                      tooltip="Show filters"
                    />
                  </Box>
                </Card.Header.Actions>
              </Card.Header>
              <Card.Body grow>
                <ResourceGroupEmployeesTable
                  clientId={clientId}
                  clientApplicationId={applicationId}
                  dateRange={dateRange}
                />
              </Card.Body>
            </Card>
          </div>
        </div>
      </Responsive>

      <SlideInPanel
        isOpen={isFilterPanelOpen}
        onClose={handleCloseFilterPanel}
        onCancel={handleFiltersReset}
        onApply={handleApplyFilters}
        cancelButtonText={areFiltersActive ? "Clear filters" : undefined}
        applyButtonText="Apply filters"
        title="Application Dashboard Filters"
      >
        <ApplicationDashboardFilterForm
          ref={filterFormRef}
          onFilterChange={handleFilterChange}
          values={filterValues}
          errorsAPI={undefined}
        />
      </SlideInPanel>
    </div>
  );
};
